import env from '@/config/env';

import { portalSlugVariants } from '@/utils/infrastructure/utilities/portalPath';
import { attachAuthErrorBody } from './portalAuthErrors';

export { isPortalAccessDenied } from './portalAuthErrors';

const toBasic = (user, secret) => `Basic ${btoa(`${user}:${secret}`)}`;

/**
 * Mint / refresh portal credentials with window.fetch so a 401 cannot enter
 * umi-request's refresh queue (that queue keys retries by timestamp and
 * loops /auth/automy + /auth/refreshToken forever).
 */
export async function fetchAutomySignIn(accessToken, headers = {}) {
  const res = await fetch(`${env.API_URL}/auth/automy`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      clientId: env.CLIENT_ID,
      Authorization: toBasic('automy', accessToken),
      ...headers,
    },
  });
  if (!res.ok) {
    throw await attachAuthErrorBody(res, 'auth/automy');
  }
  return res.json();
}

const lookupPortal = async (companyCode, portalCode) => {
  const res = await fetch(`${env.API_URL}/auth/portal`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      clientId: env.CLIENT_ID,
      Authorization: toBasic(companyCode, portalCode),
    },
  });
  if (!res.ok) {
    throw await attachAuthErrorBody(res, 'auth/portal');
  }
  return res.json();
};

/** Public portal lookup. Tries spaced and hyphenated slugs — shortCodes may contain blanks. */
export async function fetchPortalAuth(companyCode, portalCode) {
  const companies = portalSlugVariants(companyCode);
  const portals = portalSlugVariants(portalCode);
  let lastError = null;

  for (let i = 0; i < companies.length; i += 1) {
    for (let j = 0; j < portals.length; j += 1) {
      try {
        return await lookupPortal(companies[i], portals[j]);
      } catch (error) {
        lastError = error;
        if (error?.status !== 400 && error?.status !== 404) throw error;
      }
    }
  }

  throw lastError || new Error('auth/portal');
}

export async function fetchAutomyRefresh(token) {
  if (!token?.accessToken || !token?.refreshToken) return null;
  const res = await fetch(`${env.API_URL}/auth/refreshToken`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      clientId: env.CLIENT_ID,
    },
    body: JSON.stringify({
      accessToken: token.accessToken,
      refreshToken: token.refreshToken,
    }),
  });
  if (!res.ok) return null;
  const body = await res.json();
  return body?.accessToken ? body : null;
}
